import { css } from '@emotion/react';
import { useState, type ReactNode, useEffect } from 'react';

import { useId } from '@toolbox/hooks';
import { type ClassesPropOf, makeStyles } from '@toolbox/style';
import { Step, useTrustIcon, useWidgetContext } from '@widget/core';
import { Box, Grid, GridItem, Text, Link } from '../toolbox/components';
import {
  ActionButton,
  PhoneNumberField,
  QuestionLayout,
  SwitchField,
  TextInputField,
  TrustIcons,
  type USPItemProps,
} from '../components';
import { ValuationBackground } from '@widget/components/ValuationBackgound';
import { PulloverSlider } from '@widget/components/PulloverSlider';
import { useUspText } from '@widget/core/UspTextProvider';
import { DynamicUspList } from '@widget/components/UspList/DynamicUspList';
import { LAYOUT_HEADER_TYPE } from '@widget/storyblok/contants';
import { renderComponent } from '@widget/storyblok/renderComponent';
import { DynamicUspListOld } from '@widget/components/UspList/DynamicUspListOld';

import type { LinkProps, TextProps } from '../toolbox/components';
import type { StepProps } from '@widget/core';
import type { Richtext } from 'storyblok-js-client';
import type { BoxSchema } from '@widget/storyblok/schemas';

const ANIMATION_DELAY = 800;

const useStyles = makeStyles(({ theme }) => ({
  root: css`
    display: flex;
  `,
  form: css`
    margin: auto;
  `,
  footer: css`
    text-align: center;
    margin-top: 0.5rem;

    @media (min-width: ${theme.breakpoints.md}) {
      margin-top: 2rem;
    }
  `,
  actionBtn: css`
    max-width: 300px;

    &.large {
      line-height: ${theme.lineHeights.md};
    }

    @media (max-width: ${theme.breakpoints.md}) {
      max-width: 100%;
      width: 100%;
    }
  `,
  actionBtnSubText: css`
    font-size: ${theme.fontSizes.f3};
    font-weight: ${theme.fontWeights.normal};
  `,
  disclaimer: css`
    color: ${theme.palette.colors.gray['400']};
    margin-top: ${theme.spacing(4)};
    margin-left: auto;
    margin-right: auto;
    max-width: 538px;

    a {
      text-decoration: none;
      color: inherit;
      cursor: pointer;
      font-weight: ${theme.fontWeights.bold};
      padding: ${theme.spacing(1)};
      border-bottom: 3px solid ${theme.palette.colors.yellow[400]};
    }
  `,
  stepAnimationContainer: css`
    width: 100%;
    overflow: hidden;
    display: flex;
  `,
  trustIconsContainer: css`
    display: flex;
    gap: ${theme.spacing(4)};
    margin-top: ${theme.spacing(4)};
    justify-content: center;
  `,
  uspContainer: css`
    display: flex;
    width: 100%;
    justify-content: center;
    padding: 0 1rem;
    column-gap: ${theme.spacing(8)};
    margin-top: ${theme.spacing(4)};
  `,
  uspWrapper: css`
    width: 100%;
    display: flex;
    justify-content: center;
  `,
  uspContainerVertical: css`
    flex-direction: column;
    align-items: flex-start;
    padding: 0.5rem 1rem;
    row-gap: ${theme.spacing(4)};
    ${theme.media.up('sm')} {
      width: auto;
    }
  `,
  textWrapper: css`
    text-align: center;
    line-height: 20px;
    @media (min-width: ${theme.breakpoints.md}) {
      text-align: left;
      margin-top: 0.5rem;
    }
  `,
  gridWrapper: css`
    margin: 0 -0.5rem;
  `,
  salutation: css`
    margin-bottom: 0.5rem;

    @media (min-width: ${theme.breakpoints.md}) {
      margin-bottom: 1.5rem;
      margin-top: 1.25rem;
    }
  `,
}));

const textProps: TextProps = {
  variant: 'grey',
  textAlign: 'center',
  fontSize: '14px',
  display: 'inline',
  lineHeight: '20px',
  color: 'rgba(0, 75, 84, 1)',
};

const linkProps: LinkProps = {
  target: 'blank',
  color: 'rgba(9, 95, 190, 1)',
  fontWeight: '600',
  variant: 'grey',
  display: 'inline',
};

type ContactStepFields = Partial<{
  title: string;
  description: string;
  nameLabel: string;
  firstNameLabel: string;
  lastNameLabel: string;
  phoneLabel: string;
  phoneTooltip: string;
  emailLabel: string;
  emailTooltip: string;
  buttonText: string;
  buttonSubText: string;
  closeButtonText?: string;
  salutationOptions?: string[];
  icon?: ReactNode;
  socialProofMessage?: Richtext;
  hideHomeIcon?: boolean;
  additionalCheckboxes?: BoxSchema[] | null;
}>;

type USPRenderProps = {
  uspLayout: 'horizontal' | 'vertical' | null;
  hideUSP?: boolean | null;
  upsItems?: USPItemProps[] | undefined | null;
  localizedUspLabel?: string | undefined | null;
  classes: ClassesPropOf<typeof useStyles>;
};

type ContactStepProps = StepProps & ContactStepFields;

const defaultValues: ContactStepFields = {
  title: 'Wer soll die kostenlose Immobilienbewertung erhalten?',
  description:
    'Im erstellten Bericht wird Ihnen der Wert Ihrer Immobilie genannt sowie erklärt wie wir auf diesen Wert kommen.',
  nameLabel: 'Name',
  firstNameLabel: 'Vorname',
  lastNameLabel: 'Nachname',
  phoneLabel: 'Telefonnummer',
  phoneTooltip:
    'Wir melden uns in Kürze bei Ihnen, um Angaben für eine optimierte Bewertung zu erfragen und ein Verkaufs- oder Kaufinteresse zu besprechen',
  emailLabel: 'E-Mail-Adresse',
  emailTooltip:
    'Sie erhalten in Kürze Ihre persönliche Immobilienbewertung sowie weitere interessante Angebote auf die hinterlegte E-Mail-Adresse',
  buttonText: 'Bewertung erhalten',
  buttonSubText: 'Kostenlos & Unverbindlich',
};

const USPTextRender = ({
  uspLayout,
  hideUSP,
  upsItems,
  localizedUspLabel,
  classes,
}: USPRenderProps): JSX.Element => {
  if (hideUSP) {
    return <></>;
  }

  return uspLayout === 'vertical' ? (
    <div css={classes.uspWrapper as any}>
      <div css={[classes.uspContainer, classes.uspContainerVertical] as any[]}>
        <DynamicUspListOld upsItems={upsItems} localizedUspLabel={localizedUspLabel} />
      </div>
    </div>
  ) : (
    <div css={classes.uspContainer as any}>
      <DynamicUspListOld
        upsItems={upsItems}
        localizedUspLabel={localizedUspLabel}
        forceMobileMode
      />
    </div>
  );
};

export const ContactStepWithSlider = ({
  title = defaultValues.title,
  firstNameLabel = defaultValues.firstNameLabel,
  lastNameLabel = defaultValues.lastNameLabel,
  phoneLabel = defaultValues.phoneLabel,
  phoneTooltip = defaultValues.phoneTooltip,
  emailLabel = defaultValues.emailLabel,
  emailTooltip = defaultValues.emailTooltip,
  buttonText = defaultValues.buttonText,
  buttonSubText,
  icon,
  closeButtonText,
  salutationOptions = ['Herr', 'Frau'],
  socialProofMessage,
  hideHomeIcon,
  uspLayout = 'horizontal',
  hideUSP,
  swapUspTextAndTrustIcons = false,
  headerType,
  additionalCheckboxes,
  ...stepProps
}: ContactStepProps): JSX.Element => {
  const classes = useStyles();
  const trustIconsList = useTrustIcon();
  const { uspTextItems } = useUspText();
  const [isAnimated, setIsAnimated] = useState(false);
  const [showFullStep, setShowFullStep] = useState(false);
  const widget = useWidgetContext();
  const formData = widget.getFormData();

  const emailTooltipId = useId();
  const phoneTooltipId = useId();

  useEffect(() => {
    const modal = document.querySelector<HTMLElement>('[data-element="modal"]');
    const showFullStep = () => {
      setShowFullStep(true);
    };
    const timeout = window.setTimeout(() => {
      setIsAnimated(true);
    }, ANIMATION_DELAY);

    modal?.addEventListener('scroll', showFullStep);

    return () => {
      modal?.removeEventListener('scroll', showFullStep);
      window.clearTimeout(timeout);
    };
  }, []);

  const splitedAddress = (formData.zipCode as string)?.split(',');
  const address = splitedAddress?.slice(0, splitedAddress.length - 1).join(',');

  const handleFocusInput = () => {
    setShowFullStep(true);
  };

  const selectedProperty = (formData.property as string)?.replaceAll('Kauf_', '');

  return (
    <PulloverSlider
      isAnimated={isAnimated}
      forceEndAnimation={showFullStep}
      background={
        <ValuationBackground
          address={address}
          propertyType={selectedProperty}
          showIcon={!hideHomeIcon}
        />
      }
    >
      <Step {...stepProps}>
        <QuestionLayout
          title={title}
          closeButtonText={closeButtonText}
          maxW="xl"
          css={classes.root as any}
          socialProofMessage={
            headerType === LAYOUT_HEADER_TYPE.SOCIAL_PROOF_MESSAGE ? undefined : socialProofMessage
          }
          preventAutoFocus
          mode="mini"
        >
          <div css={classes.form as any}>
            <Box css={classes.salutation as any}>
              <SwitchField
                width="200px"
                name="contactForm.salutation"
                options={salutationOptions}
                required
                variant="mini"
                errorMessage="Bitte wählen Sie eine Anrede"
                keepErrorSpace={false}
                onFocus={handleFocusInput}
              />
            </Box>
            <Box>
              <Grid
                justifyContent="space-between"
                alignItems="flex-top"
                mdGap={[6, 3]}
                css={classes.gridWrapper as any}
              >
                <GridItem xs={6}>
                  <TextInputField
                    required
                    name="contactForm.firstName"
                    label={firstNameLabel}
                    onFocus={handleFocusInput}
                    keepErrorSpace={false}
                  />
                </GridItem>
                <GridItem xs={6}>
                  <TextInputField
                    required
                    name="contactForm.lastName"
                    label={lastNameLabel}
                    onFocus={handleFocusInput}
                    keepErrorSpace={false}
                  />
                </GridItem>
              </Grid>
            </Box>
            <Box>
              <Grid
                justifyContent="space-between"
                alignItems="flex-top"
                mdGap={[6, 3]}
                css={classes.gridWrapper as any}
              >
                <GridItem md={6}>
                  <TextInputField
                    required
                    rules={{
                      email: true,
                    }}
                    keepErrorSpace={false}
                    aria-describedby={emailTooltipId}
                    tooltipPlacement="top-end"
                    tooltipIcon="EsclamationIcon"
                    tooltip={emailTooltip}
                    type="email"
                    showTooltip={!!emailTooltipId}
                    name="contactForm.email"
                    label={emailLabel}
                  />
                </GridItem>
                <GridItem md={6}>
                  <PhoneNumberField
                    required
                    aria-describedby={phoneTooltipId}
                    tooltipPlacement="top-end"
                    name="contactForm.phone"
                    tooltipIcon="EsclamationIcon"
                    keepErrorSpace={false}
                    label={phoneLabel}
                    tooltip={phoneTooltip}
                    showTooltip={!!phoneTooltip}
                    onFocus={handleFocusInput}
                  />
                </GridItem>
              </Grid>
            </Box>
            {additionalCheckboxes?.map(renderComponent)}
            <div css={classes.footer as any}>
              <ActionButton
                css={classes.actionBtn as any}
                fullWidth
                size="large"
                submit
                submitWidget
              >
                <div>{buttonText}</div>
                {buttonSubText && <div css={classes.actionBtnSubText as any}>{buttonSubText}</div>}
              </ActionButton>
              <Box mt={4}>
                <Grid justifyContent="center">
                  <GridItem md={8}>
                    <div css={classes.textWrapper as any}>
                      <Text {...textProps}>Es gelten unsere</Text>{' '}
                      <Link
                        css={{ padding: 0, display: 'inline' }}
                        href="https://www.mcmakler.de/agb"
                        {...linkProps}
                      >
                        Allgemeinen Geschäftsbedingungen.
                      </Link>{' '}
                      <Text {...textProps}>In unserer</Text>{' '}
                      <Link
                        css={{ padding: 0, display: 'inline' }}
                        href="https://www.mcmakler.de/datenschutz"
                        {...linkProps}
                      >
                        Datenschutzerklärung
                      </Link>{' '}
                      <Text {...textProps}>
                        finden Sie Informationen darüber, wie wir Ihre personenbezogenen Daten
                        verarbeiten.
                      </Text>
                    </div>
                  </GridItem>
                </Grid>
              </Box>
              {!swapUspTextAndTrustIcons && (
                <div css={classes.uspContainer as any}>
                  <USPTextRender
                    uspLayout={uspLayout}
                    upsItems={uspTextItems}
                    hideUSP={hideUSP}
                    classes={classes}
                  />
                </div>
              )}
              {trustIconsList && (
                <div css={classes.trustIconsContainer as any}>
                  <TrustIcons trustIconsList={trustIconsList} />{' '}
                </div>
              )}
              {swapUspTextAndTrustIcons && (
                <div css={classes.uspContainer as any}>
                  <USPTextRender
                    uspLayout={uspLayout}
                    upsItems={uspTextItems}
                    hideUSP={hideUSP}
                    classes={classes}
                  />
                </div>
              )}
            </div>
          </div>
        </QuestionLayout>
      </Step>
    </PulloverSlider>
  );
};
